/**
 * The console SPA's only call path to /api/cortex/console/*.
 *
 * One request builder for every route, so the bearer token, the base URL and the
 * error handling are stated once. The SPA never reads a store, never re-filters a
 * source and never decides an outcome: it renders the API's answer, whatever the
 * outcome header says.
 */
import { getConsoleConfig } from "../runtimeConfig";
import { CONSOLE_RENEWABLE_REFUSAL_REASON } from "../auth/sessionConstants";
import type {
  ConsoleAgentsBoardResponse,
  ConsoleAgentsScope,
  ConsoleAskResponse,
  ConsoleDecisionsResponse,
  ConsoleGitCheckoutsResponse,
  ConsoleHistoryResponse,
  ConsoleInstancesResponse,
  ConsoleOverviewResponse,
  ConsoleRefusal,
  ConsoleReviewResponse,
  ConsoleRuleResponse,
  ConsoleRulingChoice,
  ConsoleSessionResponse,
  ConsoleSystemActionsResponse,
  ConsoleBranding,
  ConsoleTheme,
  ConsoleIngestResponse,
} from "./types";

export type { ConsoleBranding, ConsoleTheme };

export const CONSOLE_SESSION_PATH = "/api/cortex/console/session";
export const CONSOLE_ASK_PATH = "/api/cortex/console/ask";
export const CONSOLE_ASK_STREAM_PATH = "/api/cortex/console/ask/stream";
export const CONSOLE_HISTORY_PATH = "/api/cortex/console/history";
export const CONSOLE_AGENTS_PATH = "/api/cortex/console/agents";
export const CONSOLE_OVERVIEW_PATH = "/api/cortex/console/overview";
export const CONSOLE_DECISIONS_PATH = "/api/cortex/console/decisions";
export const CONSOLE_RULE_PATH = "/api/cortex/console/rule";
export const CONSOLE_REVIEW_PATH = "/api/cortex/console/review";
export const CONSOLE_INSTANCES_PATH = "/api/cortex/console/instances";
export const CONSOLE_SYSTEM_ACTIONS_PATH = "/api/cortex/console/system-actions";
export const CONSOLE_GIT_CHECKOUTS_PATH = "/api/cortex/console/git-checkouts";
export const CONSOLE_INGEST_PATH = "/api/cortex/console/ingest";
export const CONSOLE_THEME_PATH = "/api/cortex/console/theme";
export const CONSOLE_BRANDING_PATH = "/api/cortex/console/branding";
export const CONSOLE_BRANDING_PUBLIC_PATH = "/api/cortex/console/branding/public";
export const CONSOLE_BRANDING_LOGO_PATH = "/api/cortex/console/branding/logo";
export const CONSOLE_BRANDING_FAVICON_PATH = "/api/cortex/console/branding/favicon";

/** What every call returns: the parsed body plus the status it arrived with. */
export interface ConsoleApiResult<T> {
  status: number;
  body: T | ConsoleRefusal;
}

/**
 * ``forceRefresh`` asks the token source for a renewed ID token rather than whatever
 * it holds; every existing call site passes nothing and behaves as before
 * (specs/20260916-cortex-console-session-keep-alive design.md section 5.1).
 */
export type TokenSourceOptions = { forceRefresh?: boolean };
export type TokenSource = (options?: TokenSourceOptions) => Promise<string | null>;

function baseUrl(): string {
  return (getConsoleConfig().functionAppUrl || "").replace(/\/$/, "");
}

const NO_SESSION: ConsoleRefusal = {
  response_type: "console_no_session",
  outcome_type: "blocked",
  routing: "console_sign_in_required",
  reason: "console_sign_in_required",
  message: "Your Cortex Console sign-in has expired. Sign in again to continue.",
};

/**
 * ``true`` for the one refusal a fresh token can fix: a 401 whose body reason is
 * ``blocked_expired_token``. Any other status or reason is returned to the caller as
 * the API wrote it (requirements FR-4, FR-13).
 */
export function isRenewableRefusal(status: number, body: unknown): boolean {
  return (
    status === 401 &&
    typeof body === "object" &&
    body !== null &&
    (body as { reason?: unknown }).reason === CONSOLE_RENEWABLE_REFUSAL_REASON
  );
}

/**
 * One forced refresh, then one retry of ``send`` with the new token. A second 401 of
 * any reason, a refresh that yields no token, or a non-401 first answer all fall
 * through unchanged - never more than one retry per request.
 */
async function withOneRenewal(
  getToken: TokenSource,
  send: (token: string) => Promise<Response>,
  first: Response,
  firstBody: unknown,
): Promise<{ response: Response; body: unknown }> {
  if (!isRenewableRefusal(first.status, firstBody)) {
    return { response: first, body: firstBody };
  }
  const fresh = await getToken({ forceRefresh: true });
  if (!fresh) {
    return { response: first, body: firstBody };
  }
  const retried = await send(fresh);
  return { response: retried, body: await retried.json() };
}

/**
 * The answer a person sees when the console API could not be reached at all. It is
 * given the same outcome shape as a server refusal so the Work blade has one thing to
 * render, and it names the safe-failure outcome rather than showing a blank screen.
 */
export function transportFailure(detail: string): ConsoleRefusal {
  return {
    response_type: "console_transport_failure",
    outcome_type: "safe_failure",
    routing: "console_api_unreachable",
    reason: "console_api_unreachable",
    message:
      "Cortex could not reach the knowledge library. Try again in a moment." +
      (detail ? ` (${detail})` : ""),
  };
}

async function request<T>(
  path: string,
  getToken: TokenSource,
  init?: { method?: string; body?: unknown },
): Promise<ConsoleApiResult<T>> {
  const token = await getToken();
  if (!token) {
    return { status: 401, body: NO_SESSION };
  }
  const send = (bearer: string) =>
    fetch(`${baseUrl()}${path}`, {
      method: init?.method ?? "GET",
      headers: {
        Authorization: `Bearer ${bearer}`,
        Accept: "application/json",
        ...(init?.body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      credentials: "omit",
      ...(init?.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  try {
    const first = await send(token);
    const firstBody = await first.json();
    const { response, body } = await withOneRenewal(getToken, send, first, firstBody);
    return { status: response.status, body: body as T | ConsoleRefusal };
  } catch (err) {
    return {
      status: 0,
      body: transportFailure(err instanceof Error ? err.message : "network error"),
    };
  }
}

export function fetchConsoleSession(
  getToken: TokenSource,
): Promise<ConsoleApiResult<ConsoleSessionResponse>> {
  return request<ConsoleSessionResponse>(CONSOLE_SESSION_PATH, getToken);
}

function askRequestBody(
  prompt: string,
  conversationId?: string,
): { prompt: string; conversation_id?: string } {
  return conversationId ? { prompt, conversation_id: conversationId } : { prompt };
}

export function askConsoleKnowledge(
  getToken: TokenSource,
  prompt: string,
  conversationId?: string,
): Promise<ConsoleApiResult<ConsoleAskResponse>> {
  return request<ConsoleAskResponse>(CONSOLE_ASK_PATH, getToken, {
    method: "POST",
    body: askRequestBody(prompt, conversationId),
  });
}

export type AskStreamHandlers = {
  onDelta?: (text: string) => void;
};

function streamOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return baseUrl();
}

/**
 * Same ask as ``askConsoleKnowledge``, streamed. Tokens arrive as they are written.
 * Falls back to the JSON ask when the stream host is not there.
 */
export async function askConsoleKnowledgeStream(
  getToken: TokenSource,
  prompt: string,
  trailId?: string,
  handlers: AskStreamHandlers = {},
): Promise<ConsoleApiResult<ConsoleAskResponse>> {
  const token = await getToken();
  if (!token) {
    return { status: 401, body: NO_SESSION };
  }
  const send = (bearer: string) =>
    fetch(`${streamOrigin()}${CONSOLE_ASK_STREAM_PATH}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearer}`,
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      credentials: "omit",
      body: JSON.stringify(askRequestBody(prompt, trailId)),
    });
  try {
    let response = await send(token);
    if (response.status === 401) {
      // A 401 arrives as a JSON body, not as events. The same one-renewal rule as
      // ``request`` applies here, before any stream is read: one forced refresh, one
      // retry, and whatever the retry answers is final for this ask (the JSON fallback
      // below would otherwise renew a second time for the same click).
      const firstBody = (await response.json()) as ConsoleRefusal;
      if (!isRenewableRefusal(response.status, firstBody)) {
        return { status: response.status, body: firstBody };
      }
      const fresh = await getToken({ forceRefresh: true });
      if (!fresh) {
        return { status: response.status, body: firstBody };
      }
      response = await send(fresh);
      if (!response.ok || !response.body) {
        return {
          status: response.status,
          body: (await response.json()) as ConsoleAskResponse | ConsoleRefusal,
        };
      }
    }
    if (!response.ok || !response.body) {
      return askConsoleKnowledge(getToken, prompt, trailId);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let completed: ConsoleApiResult<ConsoleAskResponse> | null = null;
    let eventName = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";
      for (const part of parts) {
        let dataLine = "";
        for (const line of part.split("\n")) {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLine += line.slice(5).trim();
          }
        }
        if (!dataLine) {
          continue;
        }
        let payload: {
          delta_text?: string;
          http_status?: number;
          payload?: ConsoleAskResponse;
          user_safe_message?: string;
        };
        try {
          payload = JSON.parse(dataLine) as typeof payload;
        } catch {
          continue;
        }
        if (eventName === "content" && payload.delta_text) {
          handlers.onDelta?.(payload.delta_text);
        }
        if (eventName === "completed" && payload.payload) {
          completed = {
            status: payload.http_status ?? 200,
            body: payload.payload,
          };
        }
        if (eventName === "error") {
          return {
            status: 0,
            body: transportFailure(payload.user_safe_message || "stream error"),
          };
        }
        eventName = "";
      }
    }
    if (completed) {
      return completed;
    }
    return askConsoleKnowledge(getToken, prompt, trailId);
  } catch {
    return askConsoleKnowledge(getToken, prompt, trailId);
  }
}

/**
 * The Agents board for this caller (harness row CC1B-12).
 *
 * ``scope`` is the only parameter the read accepts, and it is passed through rather
 * than interpreted: a caller without full clearance who asks for every stream gets the
 * server's clarification and the narrower board, which is what gets rendered.
 */
export function fetchConsoleAgents(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleAgentsBoardResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleAgentsBoardResponse>(
    `${CONSOLE_AGENTS_PATH}${query}`,
    getToken,
  );
}

/**
 * The Overview for this caller (harness row CC1B-13).
 *
 * Same ``scope`` word as the Agents board, passed through rather than interpreted:
 * the server states the scope it actually applied on the answer.
 */
export function fetchConsoleOverview(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleOverviewResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleOverviewResponse>(
    `${CONSOLE_OVERVIEW_PATH}${query}`,
    getToken,
  );
}

/**
 * The Decisions list for this caller (harness row CC2-10).
 *
 * Same ``scope`` word as the Agents board, passed through rather than interpreted: the
 * server states the scope it applied, and decides ``may_rule`` on every item.
 */
export function fetchConsoleDecisions(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleDecisionsResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleDecisionsResponse>(
    `${CONSOLE_DECISIONS_PATH}${query}`,
    getToken,
  );
}

/**
 * The Instances blade for this caller (harness row CC3-09/CC3-11).
 *
 * Same ``scope`` word as the Agents board and the Overview, passed through rather
 * than interpreted: the server states the scope it actually applied on the answer.
 */
export function fetchConsoleInstances(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleInstancesResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleInstancesResponse>(
    `${CONSOLE_INSTANCES_PATH}${query}`,
    getToken,
  );
}

/**
 * The System Action Log for this caller
 * (specs/20260918-cortex-decisions-actionable-modal/, FR-SAL-1 to FR-SAL-4).
 *
 * Same ``scope`` word as every other console read, passed through rather than
 * interpreted: the server states the scope it actually applied on the answer.
 */
export function fetchConsoleSystemActions(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleSystemActionsResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleSystemActionsResponse>(
    `${CONSOLE_SYSTEM_ACTIONS_PATH}${query}`,
    getToken,
  );
}

/**
 * The Git Checkouts blade for this caller (CC-3B, harness row CC3B-05).
 *
 * Same ``scope`` word as the Agents board, the Overview and Instances, passed through
 * rather than interpreted: the server states the scope it actually applied on the
 * answer and groups the visible checkouts by operator itself - this call never
 * computes a bucket of its own.
 */
export function fetchConsoleGitCheckouts(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleGitCheckoutsResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleGitCheckoutsResponse>(
    `${CONSOLE_GIT_CHECKOUTS_PATH}${query}`,
    getToken,
  );
}

/**
 * Rule one decision from the desk. The body carries the decision key and the person's
 * choice, nothing else: who is ruling is taken from the signed-in token on the server,
 * never from anything the SPA sends (requirements.md section 7).
 */
export function ruleConsoleDecision(
  getToken: TokenSource,
  decisionKey: string,
  choice: ConsoleRulingChoice,
): Promise<ConsoleApiResult<ConsoleRuleResponse>> {
  return request<ConsoleRuleResponse>(CONSOLE_RULE_PATH, getToken, {
    method: "POST",
    body: { decision_key: decisionKey, ...choice },
  });
}

export function fetchConsoleReview(
  getToken: TokenSource,
  scope?: ConsoleAgentsScope,
): Promise<ConsoleApiResult<ConsoleReviewResponse>> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return request<ConsoleReviewResponse>(`${CONSOLE_REVIEW_PATH}${query}`, getToken);
}

export function fetchConsoleHistory(
  getToken: TokenSource,
): Promise<ConsoleApiResult<ConsoleHistoryResponse>> {
  return request<ConsoleHistoryResponse>(CONSOLE_HISTORY_PATH, getToken);
}

export function fetchConsoleTrail(
  getToken: TokenSource,
  trailId: string,
): Promise<ConsoleApiResult<ConsoleHistoryResponse>> {
  return request<ConsoleHistoryResponse>(
    `${CONSOLE_HISTORY_PATH}/${encodeURIComponent(trailId)}`,
    getToken,
  );
}

export function fetchConsoleHistoryPage(
  getToken: TokenSource,
  limit: number,
  offset: number,
): Promise<ConsoleApiResult<ConsoleHistoryResponse>> {
  return request<ConsoleHistoryResponse>(
    `${CONSOLE_HISTORY_PATH}?limit=${limit}&offset=${offset}`,
    getToken,
  );
}

export function renameConsoleTrail(
  getToken: TokenSource,
  trailId: string,
  title: string,
): Promise<ConsoleApiResult<ConsoleHistoryResponse>> {
  return request<ConsoleHistoryResponse>(
    `${CONSOLE_HISTORY_PATH}/${encodeURIComponent(trailId)}`,
    getToken,
    { method: "PATCH", body: { title } },
  );
}

export function deleteConsoleTrail(
  getToken: TokenSource,
  trailId: string,
): Promise<ConsoleApiResult<ConsoleHistoryResponse>> {
  return request<ConsoleHistoryResponse>(
    `${CONSOLE_HISTORY_PATH}/${encodeURIComponent(trailId)}`,
    getToken,
    { method: "DELETE" },
  );
}

export function startConsoleIngest(
  getToken: TokenSource,
): Promise<ConsoleApiResult<ConsoleIngestResponse>> {
  return request<ConsoleIngestResponse>(CONSOLE_INGEST_PATH, getToken, {
    method: "POST",
    body: {},
  });
}

export function fetchConsoleIngestRun(
  getToken: TokenSource,
  runId: number | string,
): Promise<ConsoleApiResult<ConsoleIngestResponse>> {
  return request<ConsoleIngestResponse>(
    `${CONSOLE_INGEST_PATH}/${encodeURIComponent(String(runId))}`,
    getToken,
  );
}

export function fetchConsoleBranding(
  getToken: TokenSource,
): Promise<ConsoleApiResult<ConsoleBranding>> {
  return request<ConsoleBranding>(CONSOLE_BRANDING_PATH, getToken);
}

export interface ConsolePublicBranding {
  logo_path?: string;
  favicon_path?: string;
  company_name?: string;
  app_title?: string;
  description?: string;
  primary_color?: string;
  message?: string;
}

export async function fetchConsolePublicBranding(): Promise<
  ConsoleApiResult<ConsolePublicBranding>
> {
  try {
    const response = await fetch(`${baseUrl()}${CONSOLE_BRANDING_PUBLIC_PATH}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    const body = (await response.json()) as ConsolePublicBranding | ConsoleRefusal;
    return { status: response.status, body };
  } catch (err) {
    return {
      status: 0,
      body: transportFailure(err instanceof Error ? err.message : "network error"),
    };
  }
}

export async function fetchConsoleBrandingImage(
  getToken: TokenSource,
  kind: "logo" | "favicon",
): Promise<string | null> {
  const token = await getToken();
  if (!token) {
    return null;
  }
  const path = kind === "logo" ? CONSOLE_BRANDING_LOGO_PATH : CONSOLE_BRANDING_FAVICON_PATH;
  try {
    const response = await fetch(`${baseUrl()}${path}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      credentials: "omit",
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function fetchConsoleTheme(): Promise<ConsoleApiResult<ConsoleTheme>> {
  try {
    const response = await fetch(`${baseUrl()}${CONSOLE_THEME_PATH}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    const body = (await response.json()) as ConsoleTheme | ConsoleRefusal;
    return { status: response.status, body: body as ConsoleTheme };
  } catch (err) {
    return {
      status: 0,
      body: transportFailure(err instanceof Error ? err.message : "network error") as unknown as ConsoleTheme,
    };
  }
}
